import { useEffect, useState } from "react";
import { Bell, BellOff, Check, Loader2 } from "lucide-react";
import { pushSupported, subscribePush, unsubscribePush, getCurrentSubscription, testPush } from "@/lib/push";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Toggle button — subscribes the current user to web push for deal drops.
 * Lives next to NavBar bell or in user menu. Self-contained.
 */
export default function PushToggle({ compact = false }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    setSupported(pushSupported());
    if (!pushSupported() || !user) return;
    (async () => {
      const sub = await getCurrentSubscription();
      setEnabled(!!sub);
    })();
  }, [user]);

  if (!user || !supported) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await unsubscribePush();
        setEnabled(false);
        toast.success("Push alerts off");
      } else {
        await subscribePush();
        setEnabled(true);
        toast.success("🔔 Subscribed — flash deals will ping you");
        // Send confirmation push
        setTimeout(() => testPush().catch(() => {}), 800);
      }
    } catch (e) {
      toast.error(e.message || "Push setup failed");
    } finally { setBusy(false); }
  };

  if (compact) {
    return (
      <button
        data-testid="push-toggle-compact"
        onClick={toggle}
        disabled={busy}
        title={enabled ? "Disable push alerts" : "Enable push alerts"}
        className={`w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center transition-all ${enabled ? "bg-[#15FF73]" : "bg-[#FAF6E8] hover:bg-[#FFE74C]"}`}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : enabled ? <Bell size={13} strokeWidth={2.4} /> : <BellOff size={13} strokeWidth={2.4} />}
      </button>
    );
  }

  return (
    <button
      data-testid="push-toggle"
      onClick={toggle}
      disabled={busy}
      className={`btn-x ${enabled ? "btn-primary" : "btn-ghost"} !py-2 !px-4 !text-xs`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : enabled ? <Check size={12} strokeWidth={2.6} /> : <Bell size={12} strokeWidth={2.4} />}
      {enabled ? "Alerts on" : "Get deal alerts"}
    </button>
  );
}
